// SYNCED FROM: VidaReactNative/src/features/AddressVerification/utils/diffWords.ts
// Keep in sync. See SYNC.md.
/**
 * Word-level diff between two strings, computed via longest-common-subsequence
 * over a flat token stream.
 *
 * Tokens are either an alphanumeric run (`[A-Za-z0-9']+`) or a single
 * non-alphanumeric character (whitespace, comma, dash, etc. — one char per
 * token). Every token participates in the LCS, and a token is highlighted iff
 * the LCS doesn't match it. Punctuation is treated like any other "word":
 * if it's present in the same position on both sides it stays quiet, if it's
 * added or removed anywhere it gets marked — regardless of what's around it.
 *
 * Whitespace participates in the LCS too, for simplicity. The renderer tints
 * text color (not background), so any "highlighted" whitespace is invisible.
 */

export type DiffSegment = {
  text: string;
  /** True when this segment should be visually highlighted as a difference. */
  highlighted: boolean;
};

export type WordDiffResult = {
  a: DiffSegment[];
  b: DiffSegment[];
};

const WORD_REGEX = /[A-Za-z0-9']+/g;

/**
 * Split a string into LCS atoms: alphanumeric runs as single tokens, and every
 * other character as its own one-character token.
 */
const tokenize = (s: string): string[] => {
  const tokens: string[] = [];
  let cursor = 0;
  for (const match of s.matchAll(WORD_REGEX)) {
    const start = match.index ?? 0;
    const text = match[0];

    // individually push each character between the last token and the current token
    for (let i = cursor; i < start; i++) {
      tokens.push(s.charAt(i));
    }

    // push the current token
    tokens.push(text);
    cursor = start + text.length;
  }
  // individually push any remaining characters after the last token
  for (let i = cursor; i < s.length; i++) {
    tokens.push(s.charAt(i));
  }
  return tokens;
};

// Implements the longest common subsequence algorithm with Dynamic Programming (DP; essentially memoization).
// https://en.wikipedia.org/wiki/Longest_common_subsequence#Solution_for_two_sequences
// Returns two boolean arrays, one for each input string.
// The outputs are the same length as the inputs, and `true` means the token
// should be highlighted (not part of the chosen LCS path), while `false`
// means the token was matched by the LCS and should render plain.
// The algorithm is O(m * n), where m and n are the lengths of the input arrays.
const computeMarks = (
  aTokens: readonly string[],
  bTokens: readonly string[]
): { aMarks: boolean[]; bMarks: boolean[] } => {
  const m = aTokens.length;
  const n = bTokens.length;

  // Logical shape is 2D (m) by (n), stored in one flat Int32Array.
  // We treat out-of-bounds neighbors as 0, which replaces a sentinel border.
  // Stored row-major in one flat Int32Array so all DP cells are contiguous in
  // memory, which improves locality and overhead in tight loops versus `number[][]`.
  const stride = n;
  const dp = new Int32Array(m * stride);
  const getDp = (i: number, j: number): number => {
    if (i < 0 || j < 0) return 0;
    return dp[i * stride + j] ?? 0;
  };

  // Fill DP left-to-right/top-to-bottom using token indices (`i`, `j`).
  // `dp[i, j]` will be the LCS length for prefixes `aTokens[0..i]` and `bTokens[0..j]`.
  for (let i = 0; i < m; i++) {
    for (let j = 0; j < n; j++) {
      let value: number;
      if (aTokens[i] === bTokens[j]) {
        // eg ABD and BCD — the last tokens are equal, so we can add one to the pre-computed value that omits the shared last token.
        // The previous value was the LCS of AB and BC, which is 1, so the value at this cell is 2.
        value = getDp(i - 1, j - 1) + 1;
      } else {
        // eg ABC and BCE — the last tokens are not equal, so we haven't found a *longer* common subsequence.
        // We check the two pre-computed subproblems created by removing each of the last tokens independently:
        //  - ABC vs BC (2)
        //  - AB vs BCE (1)
        // The value is the maximum of the two (which is 2).
        value = Math.max(getDp(i - 1, j), getDp(i, j - 1));
      }
      dp[i * stride + j] = value;
    }
  }

  // Start by marking every token as highlighted; matched tokens get flipped to
  // false during backtracking.
  const aMarks = new Array<boolean>(m).fill(true);
  const bMarks = new Array<boolean>(n).fill(true);

  // Traverse backward through both arrays simultaneously,
  // essentially zig-zagging from bottom-right of the DP table to follow the LCS path.
  // Ties are broken by moving left (decreasing `i`),
  // biased towards highlighted tokens that are later in `a` and earlier in `b`.
  let i = m - 1;
  let j = n - 1;
  while (i >= 0 && j >= 0) {
    if (aTokens[i] === bTokens[j]) {
      aMarks[i] = false;
      bMarks[j] = false;
      i--;
      j--;
    } else if (getDp(i - 1, j) >= getDp(i, j - 1)) {
      i--;
    } else {
      j--;
    }
  }
  return { aMarks, bMarks };
};

/** Coalesce adjacent tokens that share the same highlight state. */
const buildSegments = (tokens: readonly string[], marks: readonly boolean[]): DiffSegment[] => {
  const segments: DiffSegment[] = [];
  for (let k = 0; k < tokens.length; k++) {
    const text = tokens[k];
    if (text === undefined) continue;
    const highlighted = marks[k] ?? false;
    const last = segments[segments.length - 1];
    if (last && last.highlighted === highlighted) {
      last.text += text;
    } else {
      segments.push({ text, highlighted });
    }
  }
  return segments;
};

export const diffWords = (a: string, b: string): WordDiffResult => {
  if (a === b) {
    return {
      a: a ? [{ text: a, highlighted: false }] : [],
      b: b ? [{ text: b, highlighted: false }] : [],
    };
  }
  if (!a)
    return {
      a: [],
      b: [{ text: b, highlighted: true }],
    };

  if (!b)
    return {
      a: [{ text: a, highlighted: true }],
      b: [],
    };

  const aTokens = tokenize(a);
  const bTokens = tokenize(b);
  const { aMarks, bMarks } = computeMarks(aTokens, bTokens);
  return {
    a: buildSegments(aTokens, aMarks),
    b: buildSegments(bTokens, bMarks),
  };
};
